import React from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, User, ArrowRight, Shield, Cloud, AlertTriangle, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const Blog = () => {
  const featuredPost = {
    title: 'The Complete Guide to Cybersecurity for SMEs in 2024',
    excerpt: 'Small and medium enterprises face increasing cyber threats. Learn the essential cybersecurity measures every SME should implement to protect their business.',
    author: 'Sarah Mitchell',
    date: '2024-01-15',
    category: 'Cybersecurity',
    readTime: '8 min read',
    image: 'cybersecurity-guide',
    tags: ['Cybersecurity', 'SME', 'Best Practices']
  };

  const blogPosts = [
    {
      title: 'Cloud Migration: A Step-by-Step Guide for Businesses',
      excerpt: 'Planning a move to the cloud? Our comprehensive guide covers everything from assessment to implementation for a successful cloud migration.',
      author: 'David Thompson',
      date: '2024-01-12',
      category: 'Cloud Services',
      readTime: '6 min read',
      tags: ['Cloud Migration', 'AWS', 'Business Strategy'],
      icon: Cloud
    },
    {
      title: '10 Signs Your Business Needs Managed IT Services',
      excerpt: 'Discover the key indicators that suggest your business would benefit from professional managed IT services and how to make the transition.',
      author: 'Emma Rodriguez',
      date: '2024-01-10',
      category: 'Managed IT',
      readTime: '5 min read',
      tags: ['Managed IT', 'Business Growth', 'IT Strategy'],
      icon: TrendingUp
    },
    {
      title: 'Ransomware Prevention: Protecting Your Business Data',
      excerpt: 'Ransomware attacks are on the rise. Learn how to implement effective prevention strategies and create a robust defense against these threats.',
      author: 'Michael Chen',
      date: '2024-01-08',
      category: 'Cybersecurity',
      readTime: '7 min read',
      tags: ['Ransomware', 'Data Protection', 'Security'],
      icon: Shield
    },
    {
      title: 'The Hidden Costs of IT Downtime',
      excerpt: 'Understanding the true impact of system downtime on your business and how proactive IT management can prevent costly interruptions.',
      author: 'Lisa Parker',
      date: '2024-01-05',
      category: 'Business IT',
      readTime: '4 min read',
      tags: ['Downtime', 'Business Continuity', 'ROI'],
      icon: AlertTriangle
    },
    {
      title: 'GDPR Compliance: Essential Requirements for UK Businesses',
      excerpt: 'Navigate GDPR requirements with confidence. A practical guide to data protection compliance for UK businesses in 2024.',
      author: 'James Wilson',
      date: '2024-01-03',
      category: 'Compliance',
      readTime: '9 min read',
      tags: ['GDPR', 'Compliance', 'Data Protection'],
      icon: Shield
    },
    {
      title: 'Future-Proofing Your IT Infrastructure',
      excerpt: 'Strategic approaches to building an IT infrastructure that can adapt and scale with your business growth over the coming years.',
      author: 'Sarah Mitchell',
      date: '2023-12-28',
      category: 'IT Strategy',
      readTime: '6 min read',
      tags: ['Future-Proofing', 'Scalability', 'Technology'],
      icon: TrendingUp
    }
  ];

  const categories = [
    { name: 'Cybersecurity', count: 8, color: 'bg-red-100 text-red-800' },
    { name: 'Cloud Services', count: 5, color: 'bg-blue-100 text-blue-800' },
    { name: 'Managed IT', count: 6, color: 'bg-green-100 text-green-800' },
    { name: 'Compliance', count: 4, color: 'bg-purple-100 text-purple-800' },
    { name: 'Business IT', count: 7, color: 'bg-orange-100 text-orange-800' }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-primary-light text-primary-foreground py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              IT Insights & Cybersecurity Blog
            </h1>
            <p className="text-xl lg:text-2xl text-primary-foreground/90 leading-relaxed">
              Stay informed with the latest trends, best practices, and expert insights 
              in managed IT services and cybersecurity.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">Featured Article</h2>
          </div>

          <Card className="border-none shadow-xl">
            <CardContent className="p-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-gradient-to-br from-primary/10 to-primary-light/10 p-12 flex items-center justify-center">
                  <Shield className="h-32 w-32 text-primary" />
                </div>
                <div className="p-8 lg:p-12 flex flex-col justify-center">
                  <div className="flex items-center space-x-4 mb-4">
                    <Badge className="bg-primary/10 text-primary">Featured</Badge>
                    <Badge variant="secondary">{featuredPost.category}</Badge>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-foreground mb-4">
                    {featuredPost.title}
                  </h3>
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex items-center space-x-6 text-sm text-muted-foreground mb-6">
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4" />
                      <span>{featuredPost.author}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(featuredPost.date).toLocaleDateString()}</span>
                    </div>
                    <span>{featuredPost.readTime}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {featuredPost.tags.map((tag, index) => (
                      <Badge key={index} variant="outline" className="text-xs">{tag}</Badge>
                    ))}
                  </div>
                  <Button className="w-fit">
                    Read Full Article <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Blog Posts and Sidebar */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-foreground mb-8">Latest Articles</h2>
              <div className="space-y-8">
                {blogPosts.map((post, index) => (
                  <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-0">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-gradient-to-br from-primary/10 to-primary-light/10 p-8 flex items-center justify-center">
                          <post.icon className="h-16 w-16 text-primary" />
                        </div>
                        <div className="md:col-span-2 p-6">
                          <div className="flex items-center space-x-2 mb-3">
                            <Badge variant="secondary">{post.category}</Badge>
                          </div>
                          <h3 className="text-xl font-bold text-foreground mb-3">
                            {post.title}
                          </h3>
                          <p className="text-muted-foreground mb-4">
                            {post.excerpt}
                          </p>
                          <div className="flex items-center space-x-6 text-sm text-muted-foreground mb-4">
                            <div className="flex items-center space-x-2">
                              <User className="h-4 w-4" />
                              <span>{post.author}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Calendar className="h-4 w-4" />
                              <span>{new Date(post.date).toLocaleDateString()}</span>
                            </div>
                            <span>{post.readTime}</span>
                          </div>
                          <div className="flex flex-wrap gap-2 mb-4">
                            {post.tags.map((tag, tagIndex) => (
                              <Badge key={tagIndex} variant="outline" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                          <Button variant="outline" size="sm">
                            Read More <ArrowRight className="h-4 w-4 ml-2" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Categories */}
              <Card className="border-none shadow-lg">
                <CardHeader>
                  <CardTitle>Categories</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {categories.map((category, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-foreground">{category.name}</span>
                      <Badge variant="secondary" className="text-xs">
                        {category.count}
                      </Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Newsletter Signup */}
              <Card className="border-none shadow-lg bg-gradient-to-br from-primary to-primary-light text-primary-foreground">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-3">Stay Updated</h3>
                  <p className="text-primary-foreground/90 mb-4 text-sm">
                    Subscribe to our newsletter for the latest cybersecurity insights and IT tips.
                  </p>
                  <Button variant="secondary" size="sm" className="w-full">
                    Subscribe Now
                  </Button>
                </CardContent>
              </Card>

              {/* Popular Topics */}
              <Card className="border-none shadow-lg">
                <CardHeader>
                  <CardTitle>Popular Topics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {['Cybersecurity', 'Cloud Migration', 'GDPR', 'Ransomware', 'Managed IT', 'Network Security', 'Data Protection', 'Business Continuity'].map((topic, index) => (
                      <Badge key={index} variant="outline" className="text-xs cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors">
                        {topic}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <Card className="bg-gradient-to-r from-primary to-primary-light text-primary-foreground border-none">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Need Expert IT Guidance?
              </h2>
              <p className="text-xl text-primary-foreground/90 mb-8 max-w-3xl mx-auto">
                Our blog provides valuable insights, but sometimes you need personalized advice. 
                Contact our IT experts for tailored solutions to your business challenges.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild variant="secondary" size="lg">
                  <Link to="/contact">Get Expert Consultation</Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="text-primary-foreground border-primary-foreground hover:bg-primary-foreground hover:text-primary">
                  <Link to="/services">Explore Our Services</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </Layout>
  );
};

export default Blog;